import { useState, useEffect, useCallback } from 'react';

export const useSectionAnimation = () => {
  // Initialize all sections as visible by default
  const [visibleSections, setVisibleSections] = useState(new Set(['personal', 'projects', 'blog', 'contact']));
  const [animatingSections, setAnimatingSections] = useState(new Set());

  const showSection = useCallback((sectionId) => {
    // Always trigger animation for visual emphasis, regardless if already visible
    setAnimatingSections(prev => new Set([...prev, sectionId]));
    
    // Ensure section is visible
    setVisibleSections(prev => new Set([...prev, sectionId]));
    
    // Remove from animating after animation completes
    setTimeout(() => {
      setAnimatingSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
    }, 800); // Match animation duration
  }, []);

  const hideSection = useCallback((sectionId) => {
    if (!visibleSections.has(sectionId)) return;

    // Start disappear animation
    setAnimatingSections(prev => new Set([...prev, sectionId]));
    
    // Hide section after animation
    setTimeout(() => {
      setVisibleSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
      
      setAnimatingSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
    }, 600); // Match disappear animation duration
  }, [visibleSections]);

  const toggleSection = useCallback((sectionId) => {
    if (visibleSections.has(sectionId)) {
      hideSection(sectionId);
    } else {
      showSection(sectionId);
    }
  }, [visibleSections, showSection, hideSection]);

  const isSectionVisible = useCallback((sectionId) => {
    return visibleSections.has(sectionId);
  }, [visibleSections]);

  const isSectionAnimating = useCallback((sectionId) => {
    return animatingSections.has(sectionId);
  }, [animatingSections]);

  const getSectionClasses = useCallback((sectionId) => {
    const isVisible = isSectionVisible(sectionId);
    const isAnimating = isSectionAnimating(sectionId);
    
    let classes = 'section-transition';
    
    // Show sections by default, only hide if explicitly hidden and not animating
    if (isAnimating && isVisible) {
      classes += ' genie-emerge';
    } else if (isAnimating && !isVisible) {
      classes += ' genie-disappear';
    }
    // Remove the default hiding behavior - sections are visible by default
    
    return classes;
  }, [isSectionVisible, isSectionAnimating]);

  return {
    showSection,
    hideSection,
    toggleSection,
    isSectionVisible,
    isSectionAnimating,
    getSectionClasses,
    visibleSections: Array.from(visibleSections)
  };
};